import { supabase } from "@/integrations/supabase/client";
import { isNativePlatform } from "./nativePlugins";

interface UploadPart {
  partNumber: number;
  etag: string;
}

interface MultipartUploadResult {
  success: boolean;
  videoUrl?: string;
  error?: string;
}

interface ProgressCallback {
  (progress: number, status: string): void;
}

// Use smaller chunks on mobile to avoid WebView memory issues
const WEB_PART_SIZE = 25 * 1024 * 1024; // 25MB for web
const NATIVE_PART_SIZE = 5 * 1024 * 1024; // 5MB for native (Android/iOS)
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

/**
 * Get the appropriate part size based on platform
 */
const getPartSize = (): number => {
  const partSize = isNativePlatform() ? NATIVE_PART_SIZE : WEB_PART_SIZE;
  console.log(`[VideoUpload] Using part size: ${partSize / (1024 * 1024)}MB (${isNativePlatform() ? 'native' : 'web'})`);
  return partSize;
};

/**
 * Sleep for a given number of milliseconds
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Initiates a multipart upload
 */
async function initiateMultipartUpload(
  propertyId: string,
  filename: string,
  contentType: string
): Promise<{ uploadId: string; key: string; videoUrl: string }> {
  console.log(`[VideoUpload] Initiating multipart upload for ${filename}`);
  const { data, error } = await supabase.functions.invoke('r2-multipart-initiate', {
    body: { propertyId, filename, contentType },
  });

  if (error) throw new Error(`Failed to initiate upload: ${error.message}`);
  if (!data?.success) throw new Error(data?.error || 'Failed to initiate upload');

  console.log(`[VideoUpload] Multipart upload initiated, uploadId: ${data.uploadId}`);
  return {
    uploadId: data.uploadId,
    key: data.key,
    videoUrl: data.videoUrl,
  };
}

/**
 * Gets a presigned URL for uploading a part
 */
async function getSignedPartUrl(
  key: string,
  uploadId: string,
  partNumber: number
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('r2-multipart-sign-part', {
    body: { key, uploadId, partNumber },
  });

  if (error) throw new Error(`Failed to sign part: ${error.message}`);
  if (!data?.success) throw new Error(data?.error || 'Failed to sign part');

  return data.url;
}

/**
 * Upload part using XMLHttpRequest (better for Android WebView)
 * Enhanced with case-insensitive ETag handling and Content-Type support
 */
function uploadPartXHR(url: string, chunk: Blob, partNumber: number, contentType?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    
    // Set Content-Type header to match the video file type
    // Android WebView may not auto-detect content type
    if (contentType) {
      xhr.setRequestHeader('Content-Type', contentType);
      console.log(`[VideoUpload] Setting Content-Type: ${contentType}`);
    }
    
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Try both cases for ETag - Android WebView can be inconsistent
        let etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
        
        // Log all response headers for debugging
        const allHeaders = xhr.getAllResponseHeaders();
        console.log(`[VideoUpload] Part ${partNumber} response headers:\n${allHeaders}`);
        console.log(`[VideoUpload] Part ${partNumber} uploaded via XHR, status: ${xhr.status}, ETag: ${etag}`);
        
        if (etag) {
          resolve(etag);
        } else {
          // Generate a fallback ETag if R2 doesn't return one or CORS blocks it
          // This is a workaround for WebView CORS issues with ExposeHeaders
          console.warn(`[VideoUpload] No ETag in response for part ${partNumber}, generating fallback`);
          const fallbackEtag = `"part-${partNumber}-${Date.now()}"`;
          console.log(`[VideoUpload] Using fallback ETag: ${fallbackEtag}`);
          resolve(fallbackEtag);
        }
      } else {
        // Log response body for error details
        console.error(`[VideoUpload] Part ${partNumber} failed:`, {
          status: xhr.status,
          statusText: xhr.statusText,
          response: xhr.responseText?.substring(0, 500) // Limit response size in logs
        });
        reject(new Error(`Upload failed with status: ${xhr.status} ${xhr.statusText}`));
      }
    };
    
    xhr.onerror = (e) => {
      console.error(`[VideoUpload] XHR network error for part ${partNumber}:`, {
        error: e,
        readyState: xhr.readyState,
        status: xhr.status
      });
      reject(new Error('Network error during upload'));
    };
    
    xhr.ontimeout = () => {
      console.error(`[VideoUpload] XHR timeout for part ${partNumber}`);
      reject(new Error('Upload timed out'));
    };
    
    xhr.timeout = 300000; // 5 minute timeout
    
    console.log(`[VideoUpload] Starting XHR upload for part ${partNumber}, size: ${(chunk.size / (1024 * 1024)).toFixed(2)}MB`);
    xhr.send(chunk);
  });
}

/**
 * Upload part using fetch (for web browsers)
 */
async function uploadPartFetch(url: string, chunk: Blob, partNumber: number): Promise<string> {
  console.log(`[VideoUpload] Starting fetch upload for part ${partNumber}, size: ${(chunk.size / (1024 * 1024)).toFixed(2)}MB`);
  
  const response = await fetch(url, {
    method: 'PUT',
    body: chunk,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status: ${response.status}`);
  }

  const etag = response.headers.get('ETag');
  console.log(`[VideoUpload] Part ${partNumber} uploaded via fetch, status: ${response.status}, ETag: ${etag}`);
  
  if (!etag) {
    throw new Error('No ETag returned from upload');
  }

  return etag;
}

/**
 * Uploads a single part with retry logic
 * Uses XMLHttpRequest on native platforms for better Android WebView compatibility
 */
async function uploadPart(
  url: string,
  chunk: Blob,
  partNumber: number,
  contentType?: string,
  retries: number = MAX_RETRIES
): Promise<string> {
  const useXHR = isNativePlatform();
  console.log(`[VideoUpload] uploadPart ${partNumber} using ${useXHR ? 'XMLHttpRequest' : 'fetch'}`);
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      if (useXHR) {
        return await uploadPartXHR(url, chunk, partNumber, contentType);
      } else {
        return await uploadPartFetch(url, chunk, partNumber);
      }
    } catch (err) {
      console.error(`[VideoUpload] Part ${partNumber} attempt ${attempt}/${retries} failed:`, err);
      
      if (attempt === retries) {
        throw new Error(`Failed to upload part ${partNumber} after ${retries} attempts: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
      
      // Wait before retrying with exponential backoff
      const delay = RETRY_DELAY * attempt;
      console.log(`[VideoUpload] Retrying part ${partNumber} in ${delay}ms...`);
      await sleep(delay);
    }
  }

  throw new Error(`Failed to upload part ${partNumber}`);
}

/**
 * Completes the multipart upload
 */
async function completeMultipartUpload(
  key: string,
  uploadId: string,
  parts: UploadPart[]
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('r2-multipart-complete', {
    body: { key, uploadId, parts },
  });

  if (error) throw new Error(`Failed to complete upload: ${error.message}`);
  if (!data?.success) throw new Error(data?.error || 'Failed to complete upload');

  return data.videoUrl;
}

/**
 * Aborts a multipart upload
 */
async function abortMultipartUpload(key: string, uploadId: string): Promise<void> {
  try {
    await supabase.functions.invoke('r2-multipart-abort', {
      body: { key, uploadId },
    });
  } catch (err) {
    console.error('Failed to abort multipart upload:', err);
    // Don't throw - this is cleanup
  }
}

/**
 * Main function to upload a video using multipart upload
 */
export async function uploadVideoMultipart(
  file: File,
  propertyId: string,
  onProgress?: ProgressCallback
): Promise<MultipartUploadResult> {
  let uploadId: string | null = null;
  let key: string | null = null;
  const partSize = getPartSize();

  // Log detailed upload info for debugging
  console.log(`[VideoUpload] ===== Starting Video Upload =====`);
  console.log(`[VideoUpload] Platform: ${isNativePlatform() ? 'Native (Android/iOS)' : 'Web'}`);
  console.log(`[VideoUpload] File: ${file.name}`);
  console.log(`[VideoUpload] File size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
  console.log(`[VideoUpload] Content type: ${file.type}`);
  console.log(`[VideoUpload] Part size: ${(partSize / (1024 * 1024)).toFixed(0)}MB`);
  console.log(`[VideoUpload] Total parts: ${Math.ceil(file.size / partSize)}`);
  console.log(`[VideoUpload] Property ID: ${propertyId}`);

  try {
    onProgress?.(0, 'Preparing upload...');

    // Step 1: Initiate multipart upload
    const initResult = await initiateMultipartUpload(
      propertyId,
      file.name,
      file.type
    );
    
    uploadId = initResult.uploadId;
    key = initResult.key;

    onProgress?.(5, 'Starting upload...');

    // Step 2: Calculate number of parts
    const totalParts = Math.ceil(file.size / partSize);
    const parts: UploadPart[] = [];
    let uploadedBytes = 0;

    // Step 3: Upload each part
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);
      
      console.log(`[VideoUpload] Processing part ${partNumber}/${totalParts}, bytes ${start}-${end} (${(chunk.size / (1024 * 1024)).toFixed(2)}MB)`);

      // Get signed URL for this part
      const signedUrl = await getSignedPartUrl(key, uploadId, partNumber);

      // Upload the part (pass content type for Android WebView compatibility)
      const etag = await uploadPart(signedUrl, chunk, partNumber, file.type);
      
      parts.push({ partNumber, etag });
      uploadedBytes += chunk.size;

      // Calculate progress (5% for init, 90% for upload, 5% for complete)
      const uploadProgress = 5 + Math.round((uploadedBytes / file.size) * 90);
      onProgress?.(uploadProgress, `Uploading part ${partNumber} of ${totalParts}...`);
    }

    onProgress?.(95, 'Finalizing upload...');

    // Step 4: Complete multipart upload
    const videoUrl = await completeMultipartUpload(key, uploadId, parts);

    onProgress?.(100, 'Upload complete!');

    return {
      success: true,
      videoUrl,
    };

  } catch (err: any) {
    console.error('Multipart upload error:', err);

    // Attempt to abort the upload if it was started
    if (key && uploadId) {
      await abortMultipartUpload(key, uploadId);
    }

    // Return user-friendly error messages
    let errorMessage = 'Failed to upload video';
    
    if (err.message?.includes('network') || err.message?.includes('fetch')) {
      errorMessage = 'Network error. Please check your connection and try again.';
    } else if (err.message?.includes('initiate')) {
      errorMessage = 'Could not start the upload. Please try again.';
    } else if (err.message?.includes('sign')) {
      errorMessage = 'Upload authorization failed. Please try again.';
    } else if (err.message?.includes('complete')) {
      errorMessage = 'Failed to finalize upload. Please try again.';
    } else if (err.message) {
      errorMessage = err.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Upload a thumbnail image using the existing upload flow
 * (Thumbnails are small enough for single-request upload)
 */
export async function uploadThumbnail(
  thumbnailBlob: Blob,
  propertyId: string
): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
    formData.append('propertyId', propertyId);

    // Use the existing upload function for small files
    const { data, error } = await supabase.functions.invoke('upload-video-to-r2', {
      body: formData,
    });

    if (error || !data?.success) {
      console.warn('Thumbnail upload failed:', error || data?.error);
      return null;
    }

    return data.thumbnailUrl || null;
  } catch (err) {
    console.warn('Thumbnail upload error:', err);
    return null;
  }
}

/**
 * Generate a thumbnail from a video file
 * Enhanced for Android WebView compatibility
 */
export function generateVideoThumbnail(videoFile: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    // Timeout for Android WebView issues (10 seconds)
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Thumbnail generation timed out'));
    }, 10000);
    
    const cleanup = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(video.src);
    };
    
    // Android WebView compatibility attributes
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    
    // Wait for video to have enough data before seeking
    video.oncanplay = () => {
      // Seek to 1 second or half duration (whichever is smaller)
      video.currentTime = Math.min(1, video.duration / 2);
    };
    
    video.onseeked = () => {
      // Validate video dimensions before drawing
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        cleanup();
        reject(new Error('Invalid video dimensions'));
        return;
      }
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      try {
        // Draw video frame to canvas
        context?.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          cleanup();
          if (blob && blob.size > 0) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate thumbnail blob'));
          }
        }, 'image/jpeg', 0.85);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };
    
    video.onerror = () => {
      cleanup();
      reject(new Error('Failed to load video for thumbnail generation'));
    };
    
    video.src = URL.createObjectURL(videoFile);
  });
}
